import { useTranslations } from 'use-intl'
import { motion } from 'framer-motion'

interface ShopScoreBreakdownProps {
  tasteScore: string | null
  serviceScore: string | null
  atmosphereScore: string | null
  drinksScore: string | null
  cpScore: string | null
}

/** Parse score string to number, guarding NaN and clamping to [0, 5] */
const safeParseScore = (s: string | null): number => {
  const n = parseFloat(s || '0')
  return isNaN(n) ? 0 : Math.max(0, Math.min(n, 5))
}

const getCoordinatesForValue = (val: number, angleIndex: number, maxVal = 5, r = 100, cx = 150, cy = 150) => {
  const angle = -Math.PI / 2 + (angleIndex * 2 * Math.PI) / 5
  const distance = (val / maxVal) * r
  return {
    x: cx + distance * Math.cos(angle),
    y: cy + distance * Math.sin(angle)
  }
}

export function ShopScoreBreakdown({
  tasteScore,
  serviceScore,
  atmosphereScore,
  drinksScore,
  cpScore
}: ShopScoreBreakdownProps) {
  const t = useTranslations('shopDetail.scoreBreakdown')
  
  const hasAnyScore = [tasteScore, serviceScore, atmosphereScore, drinksScore, cpScore].some(s => s !== null && !isNaN(parseFloat(s)))

  if (!hasAnyScore) return null

  const dimensions = [
    { label: t('taste', { defaultValue: 'Taste' }), value: safeParseScore(tasteScore) },
    { label: t('service', { defaultValue: 'Service' }), value: safeParseScore(serviceScore) },
    { label: t('atmosphere', { defaultValue: 'Atmosphere' }), value: safeParseScore(atmosphereScore) },
    { label: t('drinks', { defaultValue: 'Drinks' }), value: safeParseScore(drinksScore) },
    { label: t('cp', { defaultValue: 'Value (CP)' }), value: safeParseScore(cpScore) },
  ]

  const gridLevels = [1, 2, 3, 4, 5]
  
  const dataPoints = dimensions.map((d, i) => getCoordinatesForValue(d.value, i))
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ') + ' Z'

  return (
    <section className="w-full pb-8">
      <div className="bg-surface-container-lowest p-6 rounded-2xl shadow-[0_20px_40px_rgba(182,23,34,0.04)] border border-outline-variant/10">
        <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-4">
          {t('title', { defaultValue: 'Score Breakdown' })}
        </h3>
        
        <div className="flex flex-col items-center gap-4">
          {/* Radar Chart — viewBox expanded to contain labels */}
          <div className="relative w-full max-w-[280px] aspect-square mx-auto">
            <svg viewBox="-30 -30 360 360" className="w-full h-full">
              {/* Grid */}
              {gridLevels.map((level) => {
                const points = dimensions.map((_, i) => getCoordinatesForValue(level, i))
                const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ') + ' Z'
                return (
                  <path 
                    key={`grid-${level}`} 
                    d={path} 
                    className="fill-none stroke-stone-200 dark:stroke-stone-700" 
                    strokeWidth="1" 
                  />
                )
              })}
              
              {/* Axis Lines */}
              {dimensions.map((_, i) => {
                const start = getCoordinatesForValue(0, i)
                const end = getCoordinatesForValue(5, i)
                return (
                  <line 
                    key={`axis-${i}`} 
                    x1={start.x} 
                    y1={start.y} 
                    x2={end.x} 
                    y2={end.y} 
                    className="stroke-stone-200 dark:stroke-stone-700" 
                    strokeWidth="1" 
                  />
                )
              })}

              {/* Data Polygon */}
              <motion.path
                d={dataPath}
                className="fill-primary/20 stroke-primary"
                strokeWidth="2.5"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                style={{ transformOrigin: '150px 150px' }}
              />
              
              {/* Data Points */}
              {dataPoints.map((p, i) => (
                <motion.circle
                  key={`point-${i}`}
                  cx={p.x}
                  cy={p.y}
                  className="fill-white stroke-primary"
                  strokeWidth="2"
                  initial={{ r: 0 }}
                  animate={{ r: 4 }}
                  transition={{ duration: 0.4, delay: 0.8 + i * 0.1 }}
                />
              ))}

              {/* Labels */}
              {dimensions.map((d, i) => {
                const labelPos = getCoordinatesForValue(5.8, i)
                const isTop = i === 0
                const isBottom = i === 2 || i === 3
                const isLeft = i === 3 || i === 4
                const isRight = i === 1 || i === 2
                
                let textAnchor: "start" | "middle" | "end" = "middle"
                if (isLeft && !isTop && !isBottom) textAnchor = "end"
                if (isRight && !isTop && !isBottom) textAnchor = "start"
                if (isLeft && isBottom) textAnchor = "end"
                if (isRight && isBottom) textAnchor = "start"
                
                return (
                  <text
                    key={`label-${i}`}
                    x={labelPos.x}
                    y={labelPos.y + (isTop ? -5 : isBottom ? 10 : 4)}
                    textAnchor={textAnchor}
                    className="text-[11px] font-bold fill-stone-500 dark:fill-stone-400 font-['Inter']"
                  >
                    {d.label}
                  </text>
                )
              })}
            </svg>
          </div>
          
          {/* Values List — horizontal grid */}
          <div className="w-full grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-2">
            {dimensions.map((d, i) => (
              <div key={i} className="flex flex-col items-center py-2">
                <span className="text-xs text-stone-400 font-medium">{d.label}</span>
                <span className="text-lg font-bold text-stone-900 dark:text-stone-100">{d.value > 0 ? d.value.toFixed(2) : '-'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
